/**
 * 实时更新传输层 —— 手写 fetch 读取 SSE。
 *
 * 之所以不用 EventSource：它无法携带 Authorization 头，而本仓鉴权走 Bearer token
 * （getStoredAuthToken() 同时覆盖 localStorage / sessionStorage）。
 *
 * 行为：
 *   - 解析 `event:` / `data:` 字段与空行分隔的帧，忽略注释行（`: ping`）；
 *   - 任何字节都算「连接还活着」，超过 heartbeatTimeoutMs 无字节则 abort 并重连；
 *   - 断线按指数退避 + 抖动重连，上限 reconnectMaxDelayMs；
 *   - HTTP 401 / 403 / 404 / 501 视为「端点未就绪或无权访问」，只记录一次 disabled 状态后停用，
 *     不弹 toast、不重试、不制造重连风暴；
 *   - 收到全局 `logout` 事件立即断开并移除监听。
 *
 * 连接开关由 RealtimeProvider 负责；本模块只负责「被要求启动之后」的连接行为。
 */
import { getStoredAuthToken } from '@/requestErrorConfig';
import { type RealtimeConfig, realtimeConfig } from './config';
import { normalizeRealtimeEvent, type RealtimeChangeEvent } from './eventTypes';

export type RealtimeConnectionStatus =
  | 'idle'
  | 'connecting'
  | 'open'
  | 'reconnecting'
  | 'disabled'
  | 'stopped';

export interface SseFrame {
  event: string;
  data: string;
}

export interface SseStreamReader {
  read(): Promise<{ done: boolean; value?: Uint8Array | string }>;
  cancel?(): Promise<void> | void;
}

export interface SseStreamBody {
  getReader(): SseStreamReader;
}

export interface RealtimeConnectionOptions {
  url?: string;
  config?: Partial<RealtimeConfig>;
  /** 默认 getStoredAuthToken()；测试可注入 */
  getToken?: () => string | null;
  onEvent: (event: RealtimeChangeEvent) => void;
  onStatusChange?: (status: RealtimeConnectionStatus) => void;
  /** 默认全局 fetch；测试可注入 */
  fetchImpl?: typeof fetch;
  /** 抖动随机源，默认 Math.random */
  random?: () => number;
}

export interface RealtimeConnection {
  start(): void;
  stop(reason?: string): void;
  getStatus(): RealtimeConnectionStatus;
  isActive(): boolean;
}

/** 解析单个 SSE 帧（不含空行分隔符）；无 data 字段返回 null。 */
function parseSseBlock(block: string): SseFrame | null {
  let event = '';
  const dataLines: string[] = [];
  for (const line of block.split('\n')) {
    if (!line || line.startsWith(':')) continue;
    const colon = line.indexOf(':');
    const field = colon === -1 ? line : line.slice(0, colon);
    let value = colon === -1 ? '' : line.slice(colon + 1);
    if (value.startsWith(' ')) value = value.slice(1);
    if (field === 'event') {
      event = value;
    } else if (field === 'data') {
      dataLines.push(value);
    }
  }
  if (dataLines.length === 0) return null;
  return { event, data: dataLines.join('\n') };
}

/** 增量帧解析器：跨 chunk 的半帧留在 buffer 里，下一个 chunk 到达后继续拼。 */
export function createSseFrameParser(onFrame: (frame: SseFrame) => void) {
  let buffer = '';
  return {
    push(chunk: string): void {
      // 统一换行，兼容 \r\n 与单独的 \r；对整段 buffer 归一化可覆盖跨 chunk 的 \r\n
      buffer = (buffer + chunk).replace(/\r\n?/g, '\n');
      let boundary = buffer.indexOf('\n\n');
      while (boundary !== -1) {
        const block = buffer.slice(0, boundary);
        buffer = buffer.slice(boundary + 2);
        const frame = parseSseBlock(block);
        if (frame) onFrame(frame);
        boundary = buffer.indexOf('\n\n');
      }
    },
  };
}

/** 读取 SSE 流直到结束/中断。onActivity 在每次收到字节时调用（含注释心跳）。 */
export async function readSseStream(
  body: SseStreamBody,
  onFrame: (frame: SseFrame) => void,
  onActivity?: () => void,
): Promise<void> {
  const reader = body.getReader();
  const parser = createSseFrameParser(onFrame);
  const decoder = typeof TextDecoder !== 'undefined' ? new TextDecoder() : null;
  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      onActivity?.();
      if (value == null) continue;
      const text =
        typeof value === 'string'
          ? value
          : decoder
            ? decoder.decode(value, { stream: true })
            : '';
      if (text) parser.push(text);
    }
  } finally {
    try {
      await reader.cancel?.();
    } catch {
      // 主动 abort 时 cancel 可能抛错，忽略
    }
  }
}

/** 指数退避 + 抖动；导出以便单测直接验证参数。 */
export function computeReconnectDelay(
  attempt: number,
  config: Pick<
    RealtimeConfig,
    'reconnectBaseDelayMs' | 'reconnectMaxDelayMs' | 'reconnectJitterRatio'
  >,
  random: () => number = Math.random,
): number {
  const exponential = config.reconnectBaseDelayMs * 2 ** Math.max(0, attempt);
  const capped = Math.min(config.reconnectMaxDelayMs, exponential);
  const jitter = Math.max(0, Math.min(1, config.reconnectJitterRatio));
  const factor = 1 + (random() * 2 - 1) * jitter;
  return Math.max(0, Math.round(capped * factor));
}

export function createRealtimeConnection(
  options: RealtimeConnectionOptions,
): RealtimeConnection {
  const config: RealtimeConfig = { ...realtimeConfig, ...options.config };
  const url = options.url ?? config.url;
  const getToken = options.getToken ?? getStoredAuthToken;
  const random = options.random ?? Math.random;
  const fetchImpl =
    options.fetchImpl ??
    (typeof fetch !== 'undefined' ? fetch.bind(globalThis) : undefined);

  let status: RealtimeConnectionStatus = 'idle';
  let active = false;
  let disabled = false;
  let attempt = 0;
  let abortController: AbortController | null = null;
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  let heartbeatTimer: ReturnType<typeof setTimeout> | null = null;
  let lastActivityAt = 0;

  function setStatus(next: RealtimeConnectionStatus): void {
    status = next;
    options.onStatusChange?.(next);
  }

  function clearReconnectTimer(): void {
    if (reconnectTimer !== null) {
      clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }
  }

  function clearHeartbeatTimer(): void {
    if (heartbeatTimer !== null) {
      clearTimeout(heartbeatTimer);
      heartbeatTimer = null;
    }
  }

  function handleLogout(): void {
    stop('logout');
  }

  function bindLogout(): void {
    if (typeof window !== 'undefined') {
      window.addEventListener('logout', handleLogout);
    }
  }

  function unbindLogout(): void {
    if (typeof window !== 'undefined') {
      window.removeEventListener('logout', handleLogout);
    }
  }

  function abortCurrent(): void {
    const controller = abortController;
    abortController = null;
    if (controller) {
      try {
        controller.abort();
      } catch {
        // 已断开
      }
    }
  }

  function teardown(): void {
    clearReconnectTimer();
    clearHeartbeatTimer();
    unbindLogout();
    abortCurrent();
  }

  function disable(): void {
    disabled = true;
    active = false;
    teardown();
    setStatus('disabled');
  }

  function stop(_reason?: string): void {
    active = false;
    teardown();
    if (!disabled) setStatus('stopped');
  }

  function scheduleReconnect(): void {
    if (!active || disabled || reconnectTimer !== null) return;
    const delay = computeReconnectDelay(attempt, config, random);
    attempt += 1;
    setStatus('reconnecting');
    reconnectTimer = setTimeout(() => {
      reconnectTimer = null;
      void run();
    }, delay);
  }

  function armHeartbeat(): void {
    clearHeartbeatTimer();
    if (!active || disabled) return;
    heartbeatTimer = setTimeout(() => {
      heartbeatTimer = null;
      if (!active || disabled) return;
      if (Date.now() - lastActivityAt >= config.heartbeatTimeoutMs) {
        abortCurrent();
        scheduleReconnect();
        return;
      }
      armHeartbeat();
    }, config.heartbeatTimeoutMs);
  }

  function handleFrame(frame: SseFrame): void {
    lastActivityAt = Date.now();
    if (frame.event && frame.event !== 'change') return;
    let parsed: unknown;
    try {
      parsed = JSON.parse(frame.data);
    } catch {
      return;
    }
    const event = normalizeRealtimeEvent(parsed);
    if (event) options.onEvent(event);
  }

  async function run(): Promise<void> {
    if (!active || disabled) return;
    const token = getToken();
    if (!token) {
      // 没有 token 说明已登出或尚未登录：不重试，等下一次显式 start()
      stop('no-token');
      return;
    }
    if (!fetchImpl) {
      stop('no-fetch');
      return;
    }

    const controller = new AbortController();
    abortController = controller;
    setStatus('connecting');

    let response: Response;
    try {
      response = await fetchImpl(url, {
        method: 'GET',
        headers: {
          Accept: 'text/event-stream',
          Authorization: `Bearer ${token}`,
        },
        cache: 'no-store',
        signal: controller.signal,
      });
    } catch {
      if (!active || controller.signal.aborted) return;
      scheduleReconnect();
      return;
    }

    if (!active || controller.signal.aborted) return;

    if (
      response.status === 401 ||
      response.status === 403 ||
      response.status === 404 ||
      response.status === 501
    ) {
      disable();
      return;
    }
    if (!response.ok || !response.body) {
      scheduleReconnect();
      return;
    }

    if (!response.headers.get('content-type')?.toLowerCase().startsWith('text/event-stream')) {
      disable();
      return;
    }

    const openedAt = Date.now();
    setStatus('open');
    lastActivityAt = Date.now();
    armHeartbeat();

    try {
      await readSseStream(
        response.body as unknown as SseStreamBody,
        handleFrame,
        () => {
          lastActivityAt = Date.now();
        },
      );
    } catch {
      // 网络中断 / abort：由下方状态判断决定是否重连
    } finally {
      clearHeartbeatTimer();
      if (abortController === controller) abortController = null;
    }

    // 连接维持得足够久才把退避计数清零；短命连接（服务端立即断开）继续按指数退避。
    if (Date.now() - openedAt >= config.reconnectMaxDelayMs) attempt = 0;
    if (active && !disabled) scheduleReconnect();
  }

  return {
    start(): void {
      if (active || disabled) return;
      active = true;
      bindLogout();
      void run();
    },
    stop,
    getStatus(): RealtimeConnectionStatus {
      return status;
    },
    isActive(): boolean {
      return active;
    },
  };
}
